import {
  ExpenseReportAnalyticGroup,
  ExpenseStatus,
  GraphAnalyticInterval,
  useAdminReportGraphMetricsQuery,
} from "@common/utils/graphql";
import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { DashboardTile } from "../components/DashboardTile";
import { TileHeader } from "../components/TileHeader";
import { PieChart } from "@common/components/Chart/PieChart";
import { Body } from "@hypercard/web-components";
import { Doughnut } from "react-chartjs-2";
import { formatDollars } from "@common/components/utils";

// Inclusive of today and 30 days ago
const now = new Date();
const endDay = new Date(
  now.getFullYear(),
  now.getMonth(),
  now.getDate(),
  23,
  59,
  59,
  999,
);
const startDay = new Date(
  now.getFullYear(),
  now.getMonth(),
  now.getDate() - 30,
  0,
  0,
  0,
  0,
);

export const TopReimbursedTile = () => {
  const { data: adminCategoryRaw, loading: adminCategoryLoading } =
    useAdminReportGraphMetricsQuery({
      variables: {
        groupedBy: ExpenseReportAnalyticGroup.Category,
        interval: GraphAnalyticInterval.Daily,
        filters: {
          transactedAfter: startDay,
          transactedBefore: endDay,
          expenseStatus: [ExpenseStatus.Approved],
        },
      },
    });

  const adminCategories = useMemo(() => {
    if (
      adminCategoryRaw?.adminReportGraphMetrics?.__typename !==
      "QueryAdminReportGraphMetricsSuccess"
    ) {
      return undefined;
    }

    return adminCategoryRaw.adminReportGraphMetrics.data;
  }, [adminCategoryRaw]);

  const totalReimbursed = useMemo(() => {
    if (!adminCategories) {
      return 0;
    }
    return adminCategories.metrics.reduce((acc, curr) => {
      return acc + curr.values[0].value;
    }, 0);
  }, [adminCategories]);

  return (
    <DashboardTile className="flex flex-col">
      {adminCategoryLoading ? (
        <>
          <Skeleton className="h-[40px] w-[100px] rounded-xl" />
          <Skeleton className="mt-5 h-[200px] w-[490px] rounded-xl" />
        </>
      ) : adminCategories && adminCategories.metrics.length > 0 ? (
        <>
          <div>
            <TileHeader>TOP REIMBURSED CATEGORIES</TileHeader>
            <div className="mt-4 text-3xl">
              {formatDollars(totalReimbursed)}
            </div>
          </div>
          <div className="flex grow flex-row justify-between pt-[20px]">
            <PieChart
              data={adminCategories.metrics.map((d) => ({
                name: d.groupedEntityName,
                amount: d.totalValue,
              }))}
            />
          </div>
        </>
      ) : (
        <>
          <TileHeader>TOP REIMBURSED CATEGORIES</TileHeader>
          <Body size="2xl" weight="medium">
            $0
          </Body>
          <div className="h-100 mt-10 flex grow flex-row justify-start">
            <div className="size-[130px]">
              <Doughnut
                data={{
                  datasets: [
                    { data: [100], backgroundColor: "#292929", hoverOffset: 4 },
                  ],
                }}
                width={130}
                height={130}
                options={{
                  elements: {
                    arc: {
                      borderWidth: 0,
                    },
                  },
                  events: [],
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      enabled: false,
                    },
                  },
                  cutout: "75%",
                  maintainAspectRatio: false,
                }}
              />
            </div>
            <div className="ml-16 flex flex-col justify-center">
              <Body size="lg" weight="medium">
                No reimbursements yet
              </Body>
              <Body size="sm" weight="regular" className="text-white/70">
                Once you start submitting reimbursements. you'll see them
                categorized here.
              </Body>
            </div>
          </div>
        </>
      )}
    </DashboardTile>
  );
};
